import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Radar, TrendingUp, AlertTriangle, Lightbulb, Users, RefreshCw, Share2,
  Settings, Loader2, ExternalLink, BookOpen, Clock, ArrowRight, Newspaper,
  Search, CheckCircle2, XCircle, Trash2, Plus, Info, MessageSquare, Briefcase,
  History, Sliders, Check, Archive, Coffee, Globe, Mail, Phone, Calendar,
  List, ChevronUp, Star, StarOff, Pencil, X
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import type { 
  HarryFinding, HarryBriefing, HarryNotificationSettings, 
  HarrySourceConfig, HarryWatchlistTopic 
} from "@shared/schema";
import { formatDistanceToNow, format, subDays, startOfDay, endOfDay } from "date-fns";
import { HarryWatchBrief, harryBriefKey, type HarryBriefResponse } from "@/components/harry-watch-brief";

// --- Helpers ---

const CATEGORIES = [
  { id: "opportunity", label: "Opportunity", color: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20", icon: Lightbulb },
  { id: "threat", label: "Threat", color: "text-rose-400 bg-rose-400/10 border-rose-400/20", icon: AlertTriangle },
  { id: "trend", label: "Trend", color: "text-blue-400 bg-blue-400/10 border-blue-400/20", icon: TrendingUp },
  { id: "community_buzz", label: "Community Buzz", color: "text-purple-400 bg-purple-400/10 border-purple-400/20", icon: Users },
];

function getCategoryData(cat: string) {
  return CATEGORIES.find(c => c.id === cat) || { id: "other", label: cat, color: "text-slate-400 bg-slate-400/10 border-slate-400/20", icon: Info };
}

function SourceIcon({ type, className }: { type: string, className?: string }) {
  switch (type) {
    case 'reddit_rss': return <MessageSquare className={className} size={14} />;
    case 'reddit_search': return <MessageSquare className={className} size={14} />;
    case 'newsapi': return <Globe className={className} size={14} />;
    case 'google_news': return <Newspaper className={className} size={14} />;
    case 'google_search': return <Search className={className} size={14} />;
    case 'google_trends': return <TrendingUp className={className} size={14} />;
    case 'x_search': return <Users className={className} size={14} />;
    case 'web_page': return <Globe className={className} size={14} />;
    case 'hn_algolia': return <Coffee className={className} size={14} />;
    case 'rss_feed': return <Newspaper className={className} size={14} />;
    default: return <Info className={className} size={14} />;
  }
}

// --- Components ---

function FindingCard({ finding, wsId }: { finding: HarryFinding, wsId: number }) {
  const { toast } = useToast();
  const cat = getCategoryData(finding.category);
  const Icon = cat.icon;

  const dismissMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/workspaces/${wsId}/harry/findings/${finding.id}/dismiss`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/findings`] });
      toast({ title: "Finding dismissed" });
    }
  });

  const snoozeMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/workspaces/${wsId}/harry/findings/${finding.id}/snooze`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/findings`] });
      toast({ title: "Finding snoozed for 7 days" });
    }
  });

  const archiveMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/workspaces/${wsId}/harry/findings/${finding.id}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/findings`] });
      toast({ title: "Finding archived" });
    }
  });

  const routeMutation = useMutation({
    mutationFn: (vars: { agent: string, outputType: string }) => 
      apiRequest("POST", `/api/workspaces/${wsId}/harry/findings/${finding.id}/route`, vars),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/findings`] });
      toast({ title: `Routed to ${vars.agent}` });
    }
  });

  return (
    <Card data-testid={`card-finding-${finding.id}`} className="overflow-hidden border-border/40 hover:border-border/80 transition-all bg-card/30">
      <CardHeader className="p-4 pb-2 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <Badge variant="outline" className={`${cat.color} font-bold px-2 py-0.5`}>
              <Icon size={10} className="mr-1" />
              {cat.label}
            </Badge>
            <div className="flex items-center gap-1.5 min-w-0 max-w-full text-[10px] text-muted-foreground/60 font-mono bg-muted/30 px-2 py-0.5 rounded-full border border-border/20">
              <SourceIcon type={finding.sourceType} className="shrink-0" />
              <span className="truncate min-w-0" title={finding.sourceIdentifier}>{finding.sourceIdentifier}</span>
              {finding.sourceType === 'x_search' && <span className="shrink-0 text-amber-400" title="X posts found through Google search: partial coverage">partial</span>}
              <span className="shrink-0">·</span>
              <span className="shrink-0 whitespace-nowrap">{finding.sourcePublishedAt ? formatDistanceToNow(new Date(finding.sourcePublishedAt)) + " ago" : "date not given"}</span>
            </div>
          </div>
          <Badge
            data-testid={`score-relevance-${finding.id}`}
            variant="secondary"
            className="shrink-0 text-[10px] font-mono bg-blue-500/10 text-blue-400 border-blue-500/20"
          >
            {finding.relevanceScore}%
          </Badge>
        </div>
        <CardTitle className="text-sm font-bold leading-tight">{finding.title}</CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-4">
        <p data-testid={`text-summary-${finding.id}`} className="text-xs text-muted-foreground/80 leading-relaxed">
          {finding.summary}
        </p>
        
        <div className="flex items-center justify-between gap-4">
          <a 
            href={finding.sourceUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[10px] font-bold text-blue-400 hover:text-blue-300 flex items-center gap-1 uppercase tracking-wider transition-colors"
          >
            Source <ExternalLink size={10} />
          </a>
          {finding.routedToAgent && (
            <Badge data-testid={`badge-routed-${finding.id}`} variant="outline" className="text-[9px] font-bold bg-emerald-500/10 text-emerald-400 border-emerald-500/20 uppercase">
              <Check size={8} className="mr-1" />
              Sent to {finding.routedToAgent}
            </Badge>
          )}
        </div>

        <div className="pt-3 border-t border-border/30 flex flex-col gap-2">
          {/* Triage Row */}
          <div className="flex items-center gap-1.5">
            <Button 
              data-testid={`button-dismiss-${finding.id}`}
              variant="ghost" 
              size="sm" 
              className="h-7 text-[10px] text-muted-foreground/50 hover:text-rose-400 px-2 gap-1"
              onClick={() => {
                if (confirm("Dismiss this finding?")) dismissMutation.mutate();
              }}
              disabled={dismissMutation.isPending}
            >
              <XCircle size={10} /> Dismiss
            </Button>
            <Button 
              data-testid={`button-snooze-${finding.id}`}
              variant="ghost" 
              size="sm" 
              className="h-7 text-[10px] text-muted-foreground/50 hover:text-amber-400 px-2 gap-1"
              onClick={() => snoozeMutation.mutate()}
              disabled={snoozeMutation.isPending}
            >
              <Clock size={10} /> Snooze 7d
            </Button>
            <Button 
              data-testid={`button-archive-${finding.id}`}
              variant="ghost" 
              size="sm" 
              className="h-7 text-[10px] text-muted-foreground/50 hover:text-blue-400 px-2 gap-1 ml-auto"
              onClick={() => archiveMutation.mutate()}
              disabled={archiveMutation.isPending}
            >
              <Archive size={10} /> Archive
            </Button>
          </div>

          {/* Agent Routing Row */}
          <div className="flex items-center gap-1.5">
            <Button 
              data-testid={`button-route-peggy-${finding.id}`}
              variant="outline" 
              size="sm" 
              className="h-7 text-[10px] flex-1 font-bold border-muted-foreground/20 hover:border-amber-400/50 hover:bg-amber-400/5 text-muted-foreground hover:text-amber-400 transition-all"
              onClick={() => routeMutation.mutate({ agent: 'peggy', outputType: 'content_item' })}
              disabled={routeMutation.isPending}
            >
              Brief Peggy
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    data-testid={`button-route-rogers-${finding.id}`}
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-[10px] flex-1 font-bold border-muted-foreground/20 hover:border-emerald-400/50 hover:bg-emerald-400/5 text-muted-foreground hover:text-emerald-400 transition-all"
                    onClick={() => routeMutation.mutate({ agent: 'rogers', outputType: 'lead' })}
                    disabled={routeMutation.isPending || (finding.sourceType !== 'reddit_rss' && finding.sourceType !== 'reddit_search')}
                  >
                    Add to Rogers
                  </Button>
                </TooltipTrigger>
                {finding.sourceType !== 'reddit_rss' && finding.sourceType !== 'reddit_search' && (
                  <TooltipContent>
                    <p className="text-[10px]">Only available for Reddit sources</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            <Button 
              data-testid={`button-route-pete-${finding.id}`}
              variant="outline" 
              size="sm" 
              className="h-7 text-[10px] flex-1 font-bold border-muted-foreground/20 hover:border-blue-400/50 hover:bg-blue-400/5 text-muted-foreground hover:text-blue-400 transition-all"
              onClick={() => routeMutation.mutate({ agent: 'pete', outputType: 'ad_note' })}
              disabled={routeMutation.isPending}
            >
              Send to Pete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Main Page ---

export default function HarryPage() {
  const { activeWorkspaceId: wsId } = useWorkspaceStore();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("today");

  // --- Queries ---
  const { data: settings, isLoading: settingsLoading } = useQuery<HarryNotificationSettings>({
    queryKey: [`/api/workspaces/${wsId}/harry/settings`],
    enabled: !!wsId,
  });

  const { data: sourceConfigs = [], isLoading: sourcesLoading } = useQuery<HarrySourceConfig[]>({
    queryKey: [`/api/workspaces/${wsId}/harry/source-configs`],
    enabled: !!wsId,
  });

  const { data: watchlist = [], isLoading: watchlistLoading } = useQuery<HarryWatchlistTopic[]>({
    queryKey: [`/api/workspaces/${wsId}/harry/watchlist`],
    enabled: !!wsId,
  });

  const { data: todayBrief, isLoading: briefLoading } = useQuery<HarryBriefing>({
    queryKey: [`/api/workspaces/${wsId}/harry/briefings/today`],
    enabled: !!wsId,
  });

  // Filters for Today's Brief
  const [sourceFilter, setSourceFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [relevanceFilter, setRelevanceFilter] = useState("all");
  const [sortFilter, setSortFilter] = useState("relevance");

  const { data: findings = [], isLoading: findingsLoading } = useQuery<HarryFinding[]>({
    queryKey: [`/api/workspaces/${wsId}/harry/findings`, sourceFilter, categoryFilter, relevanceFilter],
    queryFn: async () => {
      let url = `/api/workspaces/${wsId}/harry/findings?excludeDismissed=true`;
      if (sourceFilter !== 'all') url += `&sourceType=${sourceFilter}`;
      if (categoryFilter !== 'all') url += `&category=${categoryFilter}`;
      if (relevanceFilter !== 'all') {
        const min = relevanceFilter === '90+' ? 90 : relevanceFilter === '80-89' ? 80 : 70;
        url += `&minScore=${min}`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    enabled: !!wsId && activeTab === "today",
  });

  const sortedFindings = useMemo(() => {
    return [...findings].sort((a, b) => {
      if (sortFilter === 'relevance') return b.relevanceScore - a.relevanceScore;
      return new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime();
    });
  }, [findings, sortFilter]);

  // --- Scan Functionality ---
  // "Scan now" (POST .../harry/scan-now): drafts Harry's watch brief first when the workspace has none, then scans. The server
  // limits it per day and refuses while a scan runs, so the button is only locked while a scan is actually running or the day's
  // manual scans are used up (the old 30-minute client cooldown started even when nothing had scanned).
  const { data: briefInfo } = useQuery<HarryBriefResponse>({ queryKey: harryBriefKey(wsId!), enabled: !!wsId });
  const [liveSteps, setLiveSteps] = useState<Array<{action: string; type: string; status: string}>>([]);

  const scanMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", `/api/workspaces/${wsId}/harry/scan-now`)).json(),
    onSuccess: (r: { status: string; briefDrafted?: boolean; used?: number; cap?: number }) => {
      toast({
        title: "Scan started",
        description: `${r.briefDrafted ? "Harry drafted a watch brief from your business profile and is scanning it now." : "Harry is scanning the channels in his brief."} Manual scans today: ${r.used ?? "?"}/${r.cap ?? "?"}.`,
      });
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/briefings/today`] });
      queryClient.invalidateQueries({ queryKey: harryBriefKey(wsId!) });
    },
    onError: (e: Error) => {
      const msg = e.message.replace(/^\d+:\s*/, "");
      let text = msg;
      try { text = JSON.parse(msg).error || msg; } catch { /* plain text */ }
      toast({ title: "Scan not started", description: text, variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/briefings/today`] });
    },
  });
  const manualLeft = briefInfo ? Math.max(0, briefInfo.usage.manualScans.cap - briefInfo.usage.manualScans.used) : null;
  const scanRunning = todayBrief?.scanStatus === 'running' && !!todayBrief?.scanStartedAt && Date.now() - new Date(todayBrief.scanStartedAt).getTime() < 30 * 60 * 1000;
  const scheduleText = briefInfo?.schedule
    ? (briefInfo.schedule.nextScanAfter ? `Next automatic scan after ${format(new Date(briefInfo.schedule.nextScanAfter), 'MMM d, h:mm a')}` : briefInfo.schedule.label)
    : "";

  // poll the day's status while a scan runs, so the page settles even if the live stream misses the final event
  useEffect(() => {
    if (!wsId || !scanRunning) return;
    const t = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/briefings/today`] });
    }, 10_000);
    return () => clearInterval(t);
  }, [wsId, scanRunning]);
  const prevStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const was = prevStatus.current;
    prevStatus.current = todayBrief?.scanStatus;
    if (!wsId || was !== 'running' || todayBrief?.scanStatus === 'running') return;
    // a scan just finished: refresh the findings and the brief's "last scan" line
    queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/findings`] });
    queryClient.invalidateQueries({ queryKey: harryBriefKey(wsId) });
  }, [wsId, todayBrief?.scanStatus]);

  useEffect(() => {
    if (!wsId) return;
    const isRunning = todayBrief?.scanStatus === 'running' || scanMutation.isPending;
    if (!isRunning) return;
    setLiveSteps([]);
    const es = new EventSource(`/api/workspaces/${wsId}/stream`, { withCredentials: true });
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.agent === 'Harry' && data.action) {
          setLiveSteps(prev => [...prev.slice(-19), { action: data.action, type: data.type || 'thought', status: data.status || 'running' }]);
          if (data.type === 'milestone' && data.status === 'completed' && String(data.action).startsWith('Brief ready')) {
            queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/briefings/today`] });
            queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/findings`] });
          }
        }
      } catch {}
    };
    return () => es.close();
  }, [wsId, todayBrief?.scanStatus, scanMutation.isPending]);

  // --- Dashboard Card logic ---
  const topFindings = sortedFindings.filter(f => f.relevanceScore >= 85).slice(0, 3);
  const unreadCount = findings.filter(f => !f.seenAt).length;

  if (!wsId) return null;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Header / Dashboard Card */}
      <div className="p-6 pb-0">
        <Card className="border-border/60 bg-gradient-to-br from-blue-500/10 via-card/50 to-card shadow-2xl overflow-hidden relative group">
          <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity pointer-events-none">
            <Radar size={200} className="text-blue-400" />
          </div>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                  <Radar size={16} className="text-blue-400" />
                </div>
                <CardTitle className="text-lg font-black uppercase tracking-tight">Harry Scout Intelligence</CardTitle>
                {unreadCount > 0 && (
                  <Badge variant="destructive" className="h-5 px-1.5 min-w-[20px] justify-center text-[10px] font-bold animate-pulse">
                    {unreadCount}
                  </Badge>
                )}
              </div>
              <CardDescription className="text-xs font-medium text-muted-foreground/60 flex items-center gap-1.5">
                {scanRunning ? (
                  <><Loader2 size={10} className="animate-spin text-blue-400" /> Scanning Google News, Reddit, search and your sites...</>
                ) : todayBrief?.scanStatus === 'completed' ? (
                  <><CheckCircle2 size={10} className="text-emerald-400" /> Intelligence brief ready for {format(new Date(), 'MMM do')}{scheduleText ? ` · ${scheduleText}` : ''}</>
                ) : (
                  <><Clock size={10} className={briefInfo?.schedule.mode === 'off' ? 'text-amber-400' : ''} /> {scheduleText || 'Loading schedule...'}</>
                )}
              </CardDescription>
            </div>
            <Button
              data-testid="button-run-scan"
              size="sm"
              className="font-bold gap-2 bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20"
              disabled={scanRunning || scanMutation.isPending || manualLeft === 0}
              title={manualLeft === 0 ? "Today's manual scans are used up" : undefined}
              onClick={() => scanMutation.mutate()}
            >
              {scanMutation.isPending || scanRunning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {scanRunning ? "Scanning..." : manualLeft === 0 ? "No scans left today" : `Scan now${manualLeft !== null ? ` (${manualLeft} left today)` : ''}`}
            </Button>
          </CardHeader>
          
          <CardContent>
            {topFindings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {topFindings.map(f => (
                  <div key={f.id} className="p-3 rounded-xl bg-background/40 border border-border/40 backdrop-blur-sm space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Badge className={`${getCategoryData(f.category).color} text-[9px] px-1.5 h-4`}>{f.category}</Badge>
                      <span className="text-[10px] font-black text-blue-400">{f.relevanceScore}%</span>
                    </div>
                    <p className="text-[11px] font-bold leading-tight truncate">{f.title}</p>
                  </div>
                ))}
              </div>
            ) : todayBrief?.scanStatus === 'running' ? (
              <div className="space-y-1 py-2">
                {liveSteps.length === 0 ? (
                  <div className="flex items-center gap-2 text-muted-foreground/40 italic text-xs">
                    <Loader2 size={12} className="animate-spin" />
                    Starting scan...
                  </div>
                ) : liveSteps.map((step, i) => (
                  <div key={i} className={`flex items-center gap-2 text-[11px] ${
                    step.status === 'completed' ? 'text-emerald-400/70' :
                    step.type === 'milestone' ? 'text-blue-400' : 'text-muted-foreground/60'
                  }`}>
                    {step.status === 'running'
                      ? <Loader2 size={10} className="animate-spin shrink-0" />
                      : <CheckCircle2 size={10} className="shrink-0" />}
                    {step.action}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-4 text-muted-foreground/40 italic text-xs">
                No critical findings to display.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} className="flex-1 flex flex-col min-h-0" onValueChange={setActiveTab}>
        <div className="px-6 border-b border-border/40 bg-card/10">
          <TabsList className="h-12 w-full justify-start bg-transparent gap-6">
            <TabsTrigger value="today" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground transition-all gap-2 px-1">
              <Radar size={14} /> Today's Brief
            </TabsTrigger>
            <TabsTrigger value="watch" data-testid="tab-watch-brief" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground transition-all gap-2 px-1">
              <Briefcase size={14} /> What Harry watches
            </TabsTrigger>
            <TabsTrigger value="blog-queue" data-testid="tab-blog-queue" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-amber-400 data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground transition-all gap-2 px-1 relative">
              <List size={14} /> Blog Queue
            </TabsTrigger>
            <TabsTrigger value="watchlist" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground transition-all gap-2 px-1">
              <Search size={14} /> Watchlist
            </TabsTrigger>
            <TabsTrigger value="sources" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground transition-all gap-2 px-1">
              <Globe size={14} /> Sources
            </TabsTrigger>
            <TabsTrigger value="history" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground transition-all gap-2 px-1">
              <History size={14} /> History
            </TabsTrigger>
            <TabsTrigger value="settings" className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground transition-all gap-2 px-1">
              <Sliders size={14} /> Settings
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
          {/* 1. Today's Brief Tab */}
          <TabsContent value="today" className="m-0 space-y-6">
            {/* Status & Filter Bar */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-3 text-[11px] font-medium text-muted-foreground/60 whitespace-nowrap">
                <span>Last scan: {briefInfo?.lastScanAt ? formatDistanceToNow(new Date(briefInfo.lastScanAt)) + " ago" : todayBrief?.scanCompletedAt ? formatDistanceToNow(new Date(todayBrief.scanCompletedAt)) + " ago" : "never"}</span>
                <span className="text-border">·</span>
                <span data-testid="text-next-scan" className={briefInfo?.schedule.mode === 'off' ? 'text-amber-400' : ''}>{scheduleText}</span>
                <span className="text-border">·</span>
                <div className="flex items-center gap-1">
                  {sourceConfigs.map(config => (
                    <div 
                      key={config.id} 
                      className={`w-1.5 h-1.5 rounded-full ${config.lastFetchStatus === 'ok' ? 'bg-emerald-400' : config.lastFetchStatus === 'error' ? 'bg-rose-400' : 'bg-slate-500/30'}`}
                      title={`${config.label}: ${config.lastFetchStatus || 'never fetched'}`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Select data-testid="filter-source" value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="h-8 text-[11px] font-bold w-[120px] bg-muted/30">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="google_news">Google News</SelectItem>
                    <SelectItem value="google_search">Google search</SelectItem>
                    <SelectItem value="google_trends">Google Trends</SelectItem>
                    <SelectItem value="reddit_search">Reddit search</SelectItem>
                    <SelectItem value="x_search">X (partial)</SelectItem>
                    <SelectItem value="rss_feed">Feeds</SelectItem>
                    <SelectItem value="reddit_rss">Subreddits</SelectItem>
                    <SelectItem value="newsapi">NewsAPI</SelectItem>
                    <SelectItem value="hn_algolia">Hacker News</SelectItem>
                  </SelectContent>
                </Select>

                <Select data-testid="filter-category" value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="h-8 text-[11px] font-bold w-[130px] bg-muted/30">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="opportunity">Opportunity</SelectItem>
                    <SelectItem value="threat">Threat</SelectItem>
                    <SelectItem value="trend">Trend</SelectItem>
                    <SelectItem value="community_buzz">Community</SelectItem>
                  </SelectContent>
                </Select>

                <Select data-testid="filter-relevance" value={relevanceFilter} onValueChange={setRelevanceFilter}>
                  <SelectTrigger className="h-8 text-[11px] font-bold w-[120px] bg-muted/30">
                    <SelectValue placeholder="Relevance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Relevance</SelectItem>
                    <SelectItem value="90+">90%+ Score</SelectItem>
                    <SelectItem value="80-89">80-89% Score</SelectItem>
                    <SelectItem value="70-79">70-79% Score</SelectItem>
                  </SelectContent>
                </Select>

                <Select data-testid="filter-sort" value={sortFilter} onValueChange={setSortFilter}>
                  <SelectTrigger className="h-8 text-[11px] font-bold w-[120px] bg-muted/30">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">Most Relevant</SelectItem>
                    <SelectItem value="recent">Most Recent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Findings Grid */}
            {findingsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="h-[280px] rounded-2xl bg-muted/20 animate-pulse border border-border/40" />
                ))}
              </div>
            ) : sortedFindings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedFindings.map(finding => (
                  <FindingCard key={finding.id} finding={finding} wsId={wsId} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-border/40 rounded-3xl bg-card/5">
                <div className="w-16 h-16 rounded-full bg-muted/20 border border-border/40 flex items-center justify-center mb-4">
                  <Radar size={24} className="text-muted-foreground/30" />
                </div>
                {scanRunning ? (
                  <>
                    <p className="text-sm font-bold text-foreground/60">Scanning Signals...</p>
                    <p className="text-xs text-muted-foreground/40 mt-1">Harry is reading Google News, Reddit, search results and your sites for the topics in his brief.</p>
                  </>
                ) : !briefInfo?.lastScanAt ? (
                  <>
                    <p className="text-sm font-bold text-foreground/60">Harry has not scanned yet</p>
                    <p className="text-xs text-muted-foreground/40 mt-1 max-w-sm">He watches the topics, competitors and channels in his brief{briefInfo?.brief ? ` (${briefInfo.brief.topics.length} topics)` : ''}. Check them, then run a scan.</p>
                    <div className="mt-4 flex gap-2">
                      <Button variant="outline" size="sm" className="font-bold" onClick={() => setActiveTab("watch")}>See what Harry watches</Button>
                      <Button size="sm" className="font-bold" disabled={scanMutation.isPending || manualLeft === 0} onClick={() => scanMutation.mutate()}>Scan now</Button>
                    </div>
                  </>
                ) : (categoryFilter !== 'all' || sourceFilter !== 'all' || relevanceFilter !== 'all') ? (
                  <>
                    <p className="text-sm font-bold text-foreground/60">No findings matching your filters</p>
                    <p className="text-xs text-muted-foreground/40 mt-1">Try broadening your filters or running a fresh scan.</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-bold text-foreground/60">Nothing new that matters this week</p>
                    <p className="text-xs text-muted-foreground/40 mt-1 max-w-sm">
                      Last scan {formatDistanceToNow(new Date(briefInfo.lastScanAt))} ago{briefInfo.lastScan ? `: ${briefInfo.lastScan.candidates} new item(s) checked, none relevant enough` : ''}. Widen Harry's topics if this keeps happening.
                    </p>
                    <Button variant="outline" size="sm" className="mt-4 font-bold" onClick={() => setActiveTab("watch")}>Edit what Harry watches</Button>
                  </>
                )}
              </div>
            )}
          </TabsContent>

          {/* What Harry watches (the watch brief Draper / the owner set) */}
          <TabsContent value="watch" className="m-0 max-w-4xl mx-auto">
            <HarryWatchBrief wsId={wsId} />
          </TabsContent>

          {/* 2. Blog Queue Tab */}
          <TabsContent value="blog-queue" className="m-0">
            <BlogQueueTab wsId={wsId} />
          </TabsContent>

          {/* 3. Watchlist Tab */}
          <TabsContent value="watchlist" className="m-0 max-w-4xl mx-auto">
            <WatchlistTab wsId={wsId} topics={watchlist} />
          </TabsContent>

          {/* 3. Sources Tab */}
          <TabsContent value="sources" className="m-0 max-w-4xl mx-auto">
            <SourcesTab wsId={wsId} configs={sourceConfigs} />
          </TabsContent>

          {/* 4. History Tab */}
          <TabsContent value="history" className="m-0 max-w-4xl mx-auto">
            <HistoryTab wsId={wsId} />
          </TabsContent>

          {/* 5. Settings Tab */}
          <TabsContent value="settings" className="m-0 max-w-2xl mx-auto">
            <SettingsTab wsId={wsId} settings={settings} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

// --- Sub-Tabs ---

// ── Blog Queue Types ──────────────────────────────────────────────────────────
interface BlogQueueItem {
  id: number;
  caption: string;
  status: string;
  priority: string;
  userNotes: string | null;
  sourceAgent: string | null;
  sourceContext: Record<string, any> | null;
  createdAt: string | null;
}

function sourceBadge(agent: string | null) {
  if (agent === "harry") return { label: "Harry", className: "bg-blue-500/10 text-blue-400 border-blue-500/20" };
  if (agent === "manual") return { label: "Manual", className: "bg-amber-500/10 text-amber-400 border-amber-500/20" };
  return { label: "Strategy", className: "bg-purple-500/10 text-purple-400 border-purple-500/20" };
}

function BlogQueueTab({ wsId }: { wsId: number }) {
  const { toast } = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [addTitle, setAddTitle] = useState("");
  const [addKeyword, setAddKeyword] = useState("");
  const [addNotes, setAddNotes] = useState("");
  const [addPriority, setAddPriority] = useState<"normal" | "high">("normal");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editKeyword, setEditKeyword] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const { data: queue = [], isLoading } = useQuery<BlogQueueItem[]>({
    queryKey: ["/api/workspaces", wsId, "blog-queue"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${wsId}/blog-queue`).then(r => r.json()),
    enabled: !!wsId,
    refetchInterval: 20_000,
  });

  const addMutation = useMutation({
    mutationFn: (vars: any) => apiRequest("POST", `/api/workspaces/${wsId}/blog-queue`, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "blog-queue"] });
      toast({ title: "Topic added to queue" });
      setShowAdd(false);
      setAddTitle(""); setAddKeyword(""); setAddNotes(""); setAddPriority("normal");
    },
    onError: (e: any) => toast({ title: "Failed to add", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...vars }: any) => apiRequest("PATCH", `/api/workspaces/${wsId}/blog-queue/${id}`, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "blog-queue"] });
      setEditingId(null);
      toast({ title: "Updated" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/workspaces/${wsId}/blog-queue/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "blog-queue"] });
      toast({ title: "Removed from queue" });
    },
  });

  const togglePriorityMutation = useMutation({
    mutationFn: ({ id, priority }: { id: number; priority: string }) =>
      apiRequest("PATCH", `/api/workspaces/${wsId}/blog-queue/${id}`, { priority }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "blog-queue"] }),
  });

  const highItems = queue.filter(q => q.priority === "high");
  const normalItems = queue.filter(q => q.priority !== "high");

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-black uppercase tracking-widest">Blog Topic Queue</h2>
          <p className="text-xs text-muted-foreground/60 mt-0.5">
            Peggy writes from this queue first — high-priority items are written before normal ones.
          </p>
        </div>
        <Button
          data-testid="button-add-blog-topic"
          size="sm"
          className="font-bold gap-2 bg-amber-500 hover:bg-amber-400 text-black shadow-lg shadow-amber-500/20"
          onClick={() => setShowAdd(true)}
        >
          <Plus size={14} /> Add Topic
        </Button>
      </div>

      {/* Add Topic Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm font-black uppercase tracking-wider">Add Blog Topic</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Blog Title / Topic *</Label>
              <Input
                data-testid="input-blog-topic-title"
                value={addTitle}
                onChange={e => setAddTitle(e.target.value)}
                placeholder="e.g. 5 Tax Deductions Freelancers Miss Every Year"
                className="h-9 bg-muted/20"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Focus Keyword (optional)</Label>
              <Input
                data-testid="input-blog-keyword"
                value={addKeyword}
                onChange={e => setAddKeyword(e.target.value)}
                placeholder="e.g. freelancer tax deductions Canada"
                className="h-9 bg-muted/20"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Notes for Peggy (optional)</Label>
              <Textarea
                data-testid="textarea-blog-notes"
                value={addNotes}
                onChange={e => setAddNotes(e.target.value)}
                placeholder="Angle, audience, specific points to include..."
                className="h-20 text-xs bg-muted/20 resize-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="toggle-priority-high"
                onClick={() => setAddPriority(p => p === "high" ? "normal" : "high")}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[11px] font-bold transition-all ${
                  addPriority === "high"
                    ? "bg-amber-500/20 border-amber-500/40 text-amber-400"
                    : "bg-muted/20 border-border/30 text-muted-foreground/50 hover:border-border/60"
                }`}
              >
                <Star size={12} /> High Priority
              </button>
              <span className="text-[10px] text-muted-foreground/40">High-priority items are written first</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button
              data-testid="button-submit-blog-topic"
              size="sm"
              className="font-bold bg-amber-500 hover:bg-amber-400 text-black"
              disabled={!addTitle.trim() || addMutation.isPending}
              onClick={() => addMutation.mutate({ title: addTitle, keyword: addKeyword, notes: addNotes, priority: addPriority, userNotes: addNotes })}
            >
              {addMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : "Add to Queue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Queue List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-xl bg-muted/20 animate-pulse border border-border/30" />)}
        </div>
      ) : queue.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-border/30 rounded-2xl bg-card/10">
          <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
            <List size={22} className="text-amber-400/60" />
          </div>
          <p className="text-sm font-bold text-foreground/60">Queue is empty</p>
          <p className="text-xs text-muted-foreground/40 mt-1 max-w-xs">
            Add topics above or run a Harry scan — Harry automatically queues blog ideas from industry signals.
          </p>
          <Button variant="outline" size="sm" className="mt-4 font-bold text-xs" onClick={() => setShowAdd(true)}>
            <Plus size={12} className="mr-1.5" /> Add your first topic
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {highItems.length > 0 && (
            <div className="space-y-2">
              <p className="text-[9px] font-black uppercase tracking-widest text-amber-400/80 flex items-center gap-1.5">
                <Star size={9} /> High Priority — written first
              </p>
              {highItems.map(item => (
                <BlogQueueCard
                  key={item.id}
                  item={item}
                  wsId={wsId}
                  editingId={editingId}
                  editTitle={editTitle}
                  editKeyword={editKeyword}
                  editNotes={editNotes}
                  setEditingId={setEditingId}
                  setEditTitle={setEditTitle}
                  setEditKeyword={setEditKeyword}
                  setEditNotes={setEditNotes}
                  onDelete={() => deleteMutation.mutate(item.id)}
                  onUpdate={(vars: any) => updateMutation.mutate({ id: item.id, ...vars })}
                  onTogglePriority={() => togglePriorityMutation.mutate({ id: item.id, priority: "normal" })}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
          {normalItems.length > 0 && (
            <div className="space-y-2">
              {highItems.length > 0 && (
                <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 flex items-center gap-1.5">
                  <List size={9} /> Normal Priority
                </p>
              )}
              {normalItems.map(item => (
                <BlogQueueCard
                  key={item.id}
                  item={item}
                  wsId={wsId}
                  editingId={editingId}
                  editTitle={editTitle}
                  editKeyword={editKeyword}
                  editNotes={editNotes}
                  setEditingId={setEditingId}
                  setEditTitle={setEditTitle}
                  setEditKeyword={setEditKeyword}
                  setEditNotes={setEditNotes}
                  onDelete={() => deleteMutation.mutate(item.id)}
                  onUpdate={(vars: any) => updateMutation.mutate({ id: item.id, ...vars })}
                  onTogglePriority={() => togglePriorityMutation.mutate({ id: item.id, priority: "high" })}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface BlogQueueCardProps {
  item: BlogQueueItem;
  wsId: number;
  editingId: number | null;
  editTitle: string;
  editKeyword: string;
  editNotes: string;
  setEditingId: (id: number | null) => void;
  setEditTitle: (v: string) => void;
  setEditKeyword: (v: string) => void;
  setEditNotes: (v: string) => void;
  onDelete: () => void;
  onUpdate: (vars: any) => void;
  onTogglePriority: () => void;
  isDeleting: boolean;
}

function BlogQueueCard({
  item, editingId, editTitle, editKeyword, editNotes,
  setEditingId, setEditTitle, setEditKeyword, setEditNotes,
  onDelete, onUpdate, onTogglePriority, isDeleting,
}: BlogQueueCardProps) {
  const isEditing = editingId === item.id;
  const src = sourceBadge(item.sourceAgent);
  const sourceUrl = item.sourceContext?.sourceUrl as string | undefined;
  const keyword = (item.sourceContext?.keyword as string | undefined) || "";

  return (
    <div
      data-testid={`card-blog-queue-${item.id}`}
      className={`rounded-xl border p-4 transition-all space-y-3 ${
        item.priority === "high"
          ? "bg-amber-500/5 border-amber-500/20 hover:border-amber-500/40"
          : "bg-card/30 border-border/30 hover:border-border/60"
      }`}
    >
      {isEditing ? (
        <div className="space-y-3">
          <Input
            data-testid={`input-edit-title-${item.id}`}
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            className="h-8 text-sm font-bold bg-muted/20"
            placeholder="Blog title"
          />
          <Input
            data-testid={`input-edit-keyword-${item.id}`}
            value={editKeyword}
            onChange={e => setEditKeyword(e.target.value)}
            className="h-8 text-xs bg-muted/20"
            placeholder="Focus keyword"
          />
          <Textarea
            data-testid={`textarea-edit-notes-${item.id}`}
            value={editNotes}
            onChange={e => setEditNotes(e.target.value)}
            className="h-16 text-xs bg-muted/20 resize-none"
            placeholder="Notes for Peggy..."
          />
          <div className="flex gap-2">
            <Button
              data-testid={`button-save-edit-${item.id}`}
              size="sm"
              className="h-7 text-[10px] font-bold bg-blue-600 hover:bg-blue-500 text-white"
              onClick={() => onUpdate({ title: editTitle, keyword: editKeyword, userNotes: editNotes })}
            >
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-[10px]"
              onClick={() => setEditingId(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p data-testid={`text-queue-title-${item.id}`} className="text-sm font-bold leading-tight truncate">{item.caption}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                <Badge variant="outline" className={`text-[9px] font-bold px-1.5 h-4 ${src.className}`}>
                  {src.label}
                </Badge>
                {item.status === "in_progress" ? (
                  <Badge variant="outline" className="text-[9px] font-bold px-1.5 h-4 bg-blue-500/10 text-blue-400 border-blue-500/20 gap-1">
                    <Loader2 size={8} className="animate-spin" /> Writing…
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[9px] font-bold px-1.5 h-4 bg-muted/30 text-muted-foreground/50 border-border/20">
                    Pending
                  </Badge>
                )}
                {keyword && (
                  <span className="text-[9px] font-mono text-muted-foreground/50 bg-muted/30 px-1.5 py-0.5 rounded">
                    {keyword}
                  </span>
                )}
                {item.createdAt && (
                  <span className="text-[9px] text-muted-foreground/40">
                    {formatDistanceToNow(new Date(item.createdAt))} ago
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {/* Priority toggle */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid={`button-toggle-priority-${item.id}`}
                      onClick={onTogglePriority}
                      className={`p-1.5 rounded-lg transition-all ${
                        item.priority === "high"
                          ? "text-amber-400 hover:bg-amber-500/10"
                          : "text-muted-foreground/30 hover:text-amber-400 hover:bg-amber-500/10"
                      }`}
                    >
                      {item.priority === "high" ? <Star size={13} /> : <StarOff size={13} />}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-[10px]">{item.priority === "high" ? "Remove high priority" : "Mark as high priority"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* Edit */}
              <button
                data-testid={`button-edit-queue-${item.id}`}
                onClick={() => {
                  setEditingId(item.id);
                  setEditTitle(item.caption);
                  setEditKeyword(keyword);
                  setEditNotes(item.userNotes || "");
                }}
                className="p-1.5 rounded-lg text-muted-foreground/30 hover:text-foreground hover:bg-muted/30 transition-all"
              >
                <Pencil size={12} />
              </button>
              {/* Dismiss */}
              <button
                data-testid={`button-dismiss-queue-${item.id}`}
                onClick={() => {
                  if (confirm("Remove this topic from the queue?")) onDelete();
                }}
                disabled={isDeleting}
                className="p-1.5 rounded-lg text-muted-foreground/30 hover:text-rose-400 hover:bg-rose-500/10 transition-all"
              >
                <X size={12} />
              </button>
            </div>
          </div>

          {/* Notes + source link row */}
          {(item.userNotes || sourceUrl) && (
            <div className="flex items-start justify-between gap-3 pt-1 border-t border-border/20">
              {item.userNotes && (
                <p className="text-[10px] text-muted-foreground/50 italic leading-relaxed flex-1">{item.userNotes}</p>
              )}
              {sourceUrl && (
                <a
                  data-testid={`link-source-${item.id}`}
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] font-bold text-blue-400 hover:text-blue-300 flex items-center gap-1 shrink-0 transition-colors"
                >
                  Source <ExternalLink size={9} />
                </a>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function WatchlistTab({ wsId, topics }: { wsId: number, topics: HarryWatchlistTopic[] }) {
  const { toast } = useToast();
  const [newTopic, setNewTopic] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newPriority, setNewPriority] = useState("monitor");

  const addMutation = useMutation({
    mutationFn: (vars: any) => apiRequest("POST", `/api/workspaces/${wsId}/harry/watchlist`, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/watchlist`] });
      setNewTopic("");
      setNewDesc("");
      toast({ title: "Topic added to watchlist" });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...vars }: any) => apiRequest("PATCH", `/api/workspaces/${wsId}/harry/watchlist/${id}`, vars),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/watchlist`] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/workspaces/${wsId}/harry/watchlist/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/watchlist`] });
      toast({ title: "Topic removed" });
    }
  });

  const atLimit = topics.length >= 10;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-black uppercase tracking-tight">Intelligence Watchlist</h3>
          <p className="text-xs text-muted-foreground/60">Define the topics, competitors, and trends Harry should scout for.</p>
        </div>
        <div data-testid="text-topic-count" className="text-right">
          <p className="text-xs font-black uppercase text-blue-400">{topics.length}/10</p>
          <p className="text-[10px] text-muted-foreground/40 font-bold">Topics Used</p>
        </div>
      </div>

      <div className="space-y-3">
        {topics.map(topic => (
          <div 
            key={topic.id} 
            data-testid={`row-topic-${topic.id}`}
            className="flex items-center gap-4 p-4 rounded-2xl bg-card/30 border border-border/40 hover:border-border/80 transition-all group"
          >
            <div className={`w-2 h-8 rounded-full ${
              topic.priorityTier === 'critical' ? 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.3)]' : 
              topic.priorityTier === 'high' ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.3)]' : 
              'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.3)]'
            }`} />
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{topic.topic}</p>
              {topic.description && <p className="text-[11px] text-muted-foreground/60 truncate">{topic.description}</p>}
            </div>

            <div className="flex items-center gap-3">
              <Select 
                data-testid={`select-priority-${topic.id}`}
                value={topic.priorityTier} 
                onValueChange={(v) => updateMutation.mutate({ id: topic.id, priorityTier: v })}
              >
                <SelectTrigger className="h-8 text-[11px] font-bold w-[100px] border-none bg-muted/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="monitor">Monitor</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-muted/20 border border-border/20">
                <span className="text-[10px] font-bold text-muted-foreground/60">Active</span>
                <Switch 
                  data-testid={`toggle-topic-${topic.id}`}
                  checked={topic.active} 
                  onCheckedChange={(v) => updateMutation.mutate({ id: topic.id, active: v })}
                  className="scale-75"
                />
              </div>

              <Button 
                data-testid={`button-delete-topic-${topic.id}`}
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-muted-foreground/30 hover:text-rose-400 hover:bg-rose-400/10"
                onClick={() => {
                  if (confirm("Remove this topic?")) deleteMutation.mutate(topic.id);
                }}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}

        {topics.length === 0 && (
          <div className="py-12 text-center bg-muted/10 border border-dashed border-border/40 rounded-2xl">
            <p className="text-xs text-muted-foreground/40 italic">Watchlist is empty. Add your first topic below.</p>
          </div>
        )}
      </div>

      <Card className="border-blue-500/20 bg-blue-500/5">
        <CardHeader className="p-4 pb-0">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            <Plus size={14} className="text-blue-400" />
            Add New Watchlist Topic
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-muted-foreground/70">Topic / Keyword</Label>
              <Input 
                data-testid="input-add-topic"
                placeholder="e.g. AI-driven marketing trends" 
                value={newTopic} 
                onChange={e => setNewTopic(e.target.value)}
                className="bg-background/50"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-muted-foreground/70">Description (Optional)</Label>
              <Input 
                data-testid="input-add-topic-desc"
                placeholder="Why track this?" 
                value={newDesc} 
                onChange={e => setNewDesc(e.target.value)}
                className="bg-background/50"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1 space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-muted-foreground/70">Scout Priority</Label>
              <div className="flex gap-2">
                {['critical', 'high', 'monitor'].map(p => (
                  <Button
                    key={p}
                    variant={newPriority === p ? 'default' : 'outline'}
                    size="sm"
                    className={`flex-1 h-8 text-[11px] font-bold capitalize ${
                      newPriority === p ? 
                        (p === 'critical' ? 'bg-rose-600 hover:bg-rose-500' : p === 'high' ? 'bg-amber-600 hover:bg-amber-500' : 'bg-emerald-600 hover:bg-emerald-500') : 
                        ''
                    }`}
                    onClick={() => setNewPriority(p)}
                  >
                    {p}
                  </Button>
                ))}
              </div>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    data-testid="button-add-topic"
                    className="h-14 px-8 mt-5 font-black uppercase tracking-widest bg-blue-600 hover:bg-blue-500 text-white"
                    disabled={atLimit || !newTopic.trim() || addMutation.isPending}
                    onClick={() => addMutation.mutate({ topic: newTopic, description: newDesc, priorityTier: newPriority })}
                  >
                    {addMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} className="mr-2" />}
                    Add Topic
                  </Button>
                </TooltipTrigger>
                {atLimit && (
                  <TooltipContent>
                    <p className="text-[10px]">Maximum of 10 topics reached</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SourcesTab({ wsId, configs }: { wsId: number, configs: HarrySourceConfig[] }) {
  const { toast } = useToast();
  const [newType, setNewType] = useState("reddit_rss");
  const [newIdentifier, setNewIdentifier] = useState("");
  const [newFeedName, setNewFeedName] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [feedPreview, setFeedPreview] = useState<{ valid: boolean; itemCount: number; sampleItems: Array<{title:string;url:string}>; error?: string } | null>(null);

  const { data: rssSuggestionsData } = useQuery<{ suggestions: Array<{ label: string; url: string; description: string }>; industry: string }>({
    queryKey: [`/api/workspaces/${wsId}/harry/rss-suggestions`],
    enabled: !!wsId,
  });

  const addMutation = useMutation({
    mutationFn: (vars: any) => apiRequest("POST", `/api/workspaces/${wsId}/harry/source-configs`, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/source-configs`] });
      setNewIdentifier("");
      toast({ title: "Source added" });
    }
  });

  const testFeedMutation = useMutation<any, Error, string>({ mutationFn: (url: string) => apiRequest('POST', `/api/workspaces/${wsId}/harry/validate-rss-feed`, { url }).then((r: any) => r.json ? r.json() : r), onSuccess: (data: any) => setFeedPreview(data), onError: () => setFeedPreview({ valid: false, itemCount: 0, sampleItems: [], error: 'Request failed' }) });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...vars }: any) => apiRequest("PATCH", `/api/workspaces/${wsId}/harry/source-configs/${id}`, vars),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/source-configs`] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/workspaces/${wsId}/harry/source-configs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/source-configs`] });
      toast({ title: "Source removed" });
    }
  });

  const autoSources = configs.filter(c => c.autoPopulated);
  const userSources = configs.filter(c => !c.autoPopulated);

  const validateReddit = (name: string) => {
    const clean = name.replace(/^r\//, '');
    return /^[a-zA-Z0-9_]{2,21}$/.test(clean);
  };

  const placeholders: Record<string, string> = {
    reddit_rss: "subreddit name (e.g. accounting)",
    newsapi: "news keyword (e.g. tax laws)",
    hn_algolia: "HN query (e.g. startup funding)",
    rss_feed: "https://example.com/feed.rss",
  };

  const SourceRow = ({ config }: { config: HarrySourceConfig }) => (
    <div 
      data-testid={`row-source-${config.id}`}
      className="flex items-center gap-4 p-4 rounded-2xl bg-card/30 border border-border/40 hover:border-border/80 transition-all group"
    >
      <div className="w-8 h-8 rounded-lg bg-muted/30 flex items-center justify-center border border-border/20">
        <SourceIcon type={config.sourceType} className="text-muted-foreground/60" />
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold truncate">{config.label}</p>
        <p className="text-[10px] text-muted-foreground/40 font-mono">{config.sourceIdentifier}</p>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-muted/10 border border-border/10">
          <div className={`w-1.5 h-1.5 rounded-full ${
            config.lastFetchStatus === 'ok' ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.3)]' : 
            config.lastFetchStatus === 'error' ? 'bg-rose-400 shadow-[0_0_8px_rgba(251,113,133,0.3)]' : 
            'bg-slate-500/30'
          }`} />
          <span className="text-[9px] font-bold text-muted-foreground/50 uppercase tracking-tighter">
            {config.lastFetchStatus || 'Pending'}
          </span>
        </div>

        <Select 
          value={config.priority} 
          onValueChange={(v) => updateMutation.mutate({ id: config.id, priority: v })}
        >
          <SelectTrigger className="h-8 text-[11px] font-bold w-[100px] border-none bg-muted/20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="monitor">Monitor</SelectItem>
          </SelectContent>
        </Select>

        <Switch 
          data-testid={`toggle-source-${config.id}`}
          checked={config.active} 
          onCheckedChange={(v) => updateMutation.mutate({ id: config.id, active: v })}
          className="scale-75"
        />

        {!config.autoPopulated && (
          <Button 
            data-testid={`button-delete-source-${config.id}`}
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-muted-foreground/30 hover:text-rose-400 hover:bg-rose-400/10"
            onClick={() => {
              if (confirm("Remove this source?")) deleteMutation.mutate(config.id);
            }}
          >
            <Trash2 size={14} />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-12">
      {/* Draper Configured */}
      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-black uppercase tracking-widest text-blue-400">Configured by Draper</h3>
          <p className="text-[10px] text-muted-foreground/60 font-medium">Automatic sources Harry identified based on your industry and brand strategy.</p>
        </div>
        <div className="space-y-2">
          {autoSources.map(c => <SourceRow key={c.id} config={c} />)}
          {autoSources.length === 0 && <p className="text-[11px] text-muted-foreground/30 italic">No automated sources configured.</p>}
        </div>
      </section>

      {/* User Added */}
      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-black uppercase tracking-widest text-emerald-400">Added by You</h3>
          <p className="text-[10px] text-muted-foreground/60 font-medium">Custom intel sources you've manually added to Harry's scouting routine.</p>
        </div>
        <div className="space-y-2">
          {userSources.map(c => <SourceRow key={c.id} config={c} />)}
          {userSources.length === 0 && <p className="text-[11px] text-muted-foreground/30 italic">You haven't added any custom sources yet.</p>}
        </div>
      </section>

      {/* Add Source Form */}
      <Card className="border-emerald-500/20 bg-emerald-500/5">
        <CardHeader className="p-4 pb-0">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            <Plus size={14} className="text-emerald-400" />
            Connect New Source
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-[180px] space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-muted-foreground/70">Type</Label>
              <Select data-testid="select-source-type" value={newType} onValueChange={v => { setNewType(v); setNewIdentifier(""); setNewFeedName(""); }}>
                <SelectTrigger className="h-10 font-bold bg-background/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reddit_rss">Reddit</SelectItem>
                  <SelectItem value="newsapi">NewsAPI</SelectItem>
                  <SelectItem value="hn_algolia">Hacker News</SelectItem>
                  <SelectItem value="rss_feed">RSS / Atom Feed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newType === "rss_feed" ? (
              <div className="flex flex-1 gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label className="text-[10px] font-bold uppercase text-muted-foreground/70">Feed Name</Label>
                  <Input
                    data-testid="input-rss-feed-name"
                    placeholder="e.g. TechCrunch"
                    value={newFeedName}
                    onChange={e => setNewFeedName(e.target.value)}
                    className="h-10 bg-background/50 text-sm"
                  />
                </div>
                <div className="flex-[2] space-y-1.5">
                  <Label className="text-[10px] font-bold uppercase text-muted-foreground/70">Feed URL</Label>
                  <Input
                    data-testid="input-source-identifier"
                    placeholder={placeholders[newType]}
                    value={newIdentifier}
                    onChange={e => { setNewIdentifier(e.target.value); setFeedPreview(null); }}
                    type="url"
                    className="h-10 bg-background/50 font-mono text-sm"
                  />
                </div>
                <Button type='button' size='sm' variant='outline' className='h-10 mt-5 whitespace-nowrap text-[10px] font-bold border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10' disabled={!newIdentifier.trim() || testFeedMutation.isPending} onClick={() => { setFeedPreview(null); testFeedMutation.mutate(newIdentifier.trim()); }} data-testid='button-test-feed'>{testFeedMutation.isPending ? <Loader2 size={12} className='animate-spin mr-1'/> : null}Test Feed</Button>
              </div>
            ) : (
              <div className="flex-1 space-y-1.5">
                <Label className="text-[10px] font-bold uppercase text-muted-foreground/70">Identifier</Label>
                <Input
                  data-testid="input-source-identifier"
                  placeholder={placeholders[newType]}
                  value={newIdentifier}
                  onChange={e => setNewIdentifier(e.target.value)}
                  className="h-10 bg-background/50 font-mono text-sm"
                />
              </div>
            )}
            <Button 
              data-testid="button-add-source"
              className="h-10 px-8 md:mt-5 font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 text-white"
              disabled={!newIdentifier.trim() || addMutation.isPending || (newType === 'reddit_rss' && !validateReddit(newIdentifier)) || (newType === 'rss_feed' && !newFeedName.trim())}
              onClick={() => {
                let ident = newIdentifier.trim();
                if (newType === 'reddit_rss') ident = ident.replace(/^r\//, '');
                const label = newType === 'reddit_rss' ? `r/${ident}` : newType === 'rss_feed' ? newFeedName.trim() : ident;
                addMutation.mutate({ sourceType: newType, sourceIdentifier: ident, label });
              }}
            >
              {addMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} className="mr-2" />}
              Connect
            </Button>
          </div>
          {feedPreview && newType === 'rss_feed' && (<div className={`mt-2 p-3 rounded-xl text-[10px] border ${feedPreview.valid ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`} data-testid='feed-preview-panel'>{feedPreview.valid ? (<><span className='text-emerald-400 font-bold'>Feed OK — {feedPreview.itemCount} items found</span><ul className='mt-1.5 space-y-0.5'>{feedPreview.sampleItems.map((item, i) => (<li key={i} className='truncate text-muted-foreground/60'>{item.title}</li>))}</ul></>) : (<span className='text-red-400 font-semibold'>Feed error: {feedPreview.error}</span>)}</div>)}
          {/* Industry RSS Suggestions */}
          {newType === "rss_feed" && rssSuggestionsData && rssSuggestionsData.suggestions.length > 0 && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowSuggestions(v => !v)}
                className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-400/70 hover:text-emerald-400 transition-colors"
                data-testid="button-toggle-rss-suggestions"
              >
                <Globe size={10} />
                {showSuggestions ? "Hide" : "Show"} suggested feeds for {rssSuggestionsData.industry || "your industry"}
              </button>
              {showSuggestions && (
                <div className="flex flex-wrap gap-2 p-3 rounded-xl bg-muted/20 border border-border/30">
                  {rssSuggestionsData.suggestions.map((feed, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`suggestion-rss-${i}`}
                      onClick={() => { setNewIdentifier(feed.url); setNewFeedName(feed.label); }}
                      title={feed.description}
                      className={`text-[10px] px-2 py-1 rounded-lg border transition-all ${
                        newIdentifier === feed.url
                          ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300"
                          : "bg-muted/30 border-border/30 text-muted-foreground/60 hover:border-emerald-500/30 hover:text-emerald-400"
                      }`}
                    >
                      {feed.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function HistoryTab({ wsId }: { wsId: number }) {
  const [range, setRange] = useState("7d");
  const [search, setSearch] = useState("");
  
  const startDate = range === "7d" ? format(subDays(new Date(), 7), 'yyyy-MM-dd') : 
                 range === "30d" ? format(subDays(new Date(), 30), 'yyyy-MM-dd') : '';

  const { data: history = [], isLoading } = useQuery<HarryFinding[]>({
    queryKey: [`/api/workspaces/${wsId}/harry/findings/history`, range, search],
    queryFn: async () => {
      let url = `/api/workspaces/${wsId}/harry/findings?excludeDismissed=false`;
      if (startDate) url += `&startDate=${startDate}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const data: HarryFinding[] = Array.isArray(json) ? json : [];
      if (search) {
        return data.filter(f => 
          f.title.toLowerCase().includes(search.toLowerCase()) || 
          f.summary.toLowerCase().includes(search.toLowerCase())
        );
      }
      return data;
    },
    enabled: !!wsId,
  });

  const grouped = useMemo(() => {
    const groups: Record<string, HarryFinding[]> = {};
    history.forEach(f => {
      if (!groups[f.briefingDate]) groups[f.briefingDate] = [];
      groups[f.briefingDate].push(f);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [history]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-2">
          <Button 
            data-testid="button-history-7d"
            variant={range === '7d' ? 'default' : 'outline'} 
            size="sm" 
            className="h-8 text-[11px] font-bold"
            onClick={() => setRange('7d')}
          >
            Last 7 Days
          </Button>
          <Button 
            data-testid="button-history-30d"
            variant={range === '30d' ? 'default' : 'outline'} 
            size="sm" 
            className="h-8 text-[11px] font-bold"
            onClick={() => setRange('30d')}
          >
            Last 30 Days
          </Button>
        </div>

        <div className="flex-1 max-w-sm relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/40" />
          <Input 
            data-testid="input-history-search"
            placeholder="Search intel archives..." 
            className="h-8 pl-9 text-[11px] font-medium bg-muted/20"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <Button 
          data-testid="button-export-csv"
          variant="outline" 
          size="sm" 
          className="h-8 text-[11px] font-bold gap-2"
          onClick={() => {
            window.location.href = `/api/workspaces/${wsId}/harry/findings/export?startDate=${startDate}`;
          }}
        >
          <Download size={14} /> Export CSV
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-blue-500" />
        </div>
      ) : grouped.length > 0 ? (
        <div className="space-y-8">
          {grouped.map(([date, items]) => (
            <div key={date} data-testid={`group-date-${date}`} className="space-y-3">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/40 flex items-center gap-3">
                {format(new Date(date), 'MMMM do, yyyy')}
                <div className="h-px flex-1 bg-border/20" />
              </h4>
              <div className="space-y-2">
                {items.map(item => (
                  <div 
                    key={item.id} 
                    data-testid={`row-history-${item.id}`}
                    className="flex items-center gap-4 p-3 rounded-xl bg-card/20 border border-border/20 hover:border-border/60 transition-all"
                  >
                    <div className="w-2 h-2 rounded-full bg-blue-500/40" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate">{item.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className={`${getCategoryData(item.category).color} text-[8px] h-3.5 px-1`}>
                          {item.category}
                        </Badge>
                        <span className="text-[9px] text-muted-foreground/40 font-mono">
                          {item.sourceType} · {item.sourceIdentifier}
                        </span>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-[10px] font-mono bg-blue-500/5 text-blue-400/60">
                      {item.relevanceScore}%
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-20 text-center border-2 border-dashed border-border/40 rounded-3xl bg-card/5">
          <History size={24} className="mx-auto text-muted-foreground/20 mb-3" />
          <p className="text-sm font-bold text-foreground/40">Intel archives are empty for this range.</p>
        </div>
      )}
    </div>
  );
}

function SettingsTab({ wsId, settings }: { wsId: number, settings?: HarryNotificationSettings }) {
  const { toast } = useToast();
  const [localSettings, setLocalSettings] = useState<Partial<HarryNotificationSettings>>({});

  useEffect(() => {
    if (settings) setLocalSettings(settings);
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (vars: any) => apiRequest("PUT", `/api/workspaces/${wsId}/harry/settings`, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/settings`] });
      toast({ title: "Settings saved", description: "Harry's scout logic has been updated." });
    }
  });

  const pauseMutation = useMutation({
    mutationFn: (pause: boolean) => apiRequest("PUT", `/api/workspaces/${wsId}/harry/settings`, { 
      digestPausedUntil: pause ? format(addDays(new Date(), 7), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") : null 
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/harry/settings`] }),
  });

  if (!settings) return null;

  const times = Array.from({ length: 48 }, (_, i) => {
    const h = Math.floor(i / 2).toString().padStart(2, '0');
    const m = (i % 2 === 0 ? '00' : '30');
    return `${h}:${m}`;
  });

  const updateField = (field: keyof HarryNotificationSettings, value: any) => {
    setLocalSettings(prev => ({ ...prev, [field]: value }));
  };

  const isPaused = settings.digestPausedUntil && new Date(settings.digestPausedUntil) > new Date();

  return (
    <div className="space-y-8">
      {/* Harry Master Toggle */}
      <div className="p-4 rounded-2xl bg-blue-500/5 border border-blue-500/20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
            localSettings.enabled ? 'bg-blue-600 border-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.4)]' : 'bg-muted border-border'
          }`}>
            <Radar className={localSettings.enabled ? 'text-white' : 'text-muted-foreground'} size={18} />
          </div>
          <div>
            <h4 className="text-sm font-black uppercase tracking-tight">Harry Agent Active</h4>
            <p className="text-[10px] text-muted-foreground/60 font-medium">Harry will run background scans and process intelligence.</p>
          </div>
        </div>
        <Switch 
          data-testid="toggle-harry-enabled"
          checked={localSettings.enabled} 
          onCheckedChange={v => updateField('enabled', v)}
        />
      </div>

      <div className="grid grid-cols-1 gap-8 opacity-90">
        {/* Email Digest */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-black uppercase tracking-widest flex items-center gap-2">
              <Mail size={12} className="text-blue-400" />
              Email Intelligence Digest
            </h4>
            <Switch 
              data-testid="toggle-email-digest"
              checked={localSettings.emailDigestEnabled} 
              onCheckedChange={v => updateField('emailDigestEnabled', v)}
              className="scale-90"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Delivery Time</Label>
              <Select 
                data-testid="select-digest-time"
                value={localSettings.emailDigestTime} 
                onValueChange={v => updateField('emailDigestTime', v)}
              >
                <SelectTrigger className="h-9 font-mono text-xs bg-muted/20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {times.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Timezone</Label>
              <Input 
                data-testid="input-digest-timezone"
                value={localSettings.emailDigestTimezone} 
                onChange={e => updateField('emailDigestTimezone', e.target.value)}
                className="h-9 font-mono text-xs bg-muted/20"
                placeholder="UTC"
              />
            </div>
          </div>
          <div className="pt-2">
            {isPaused ? (
              <Button 
                data-testid="button-resume-digest"
                variant="outline" 
                size="sm" 
                className="w-full text-[10px] font-bold text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/10"
                onClick={() => pauseMutation.mutate(false)}
              >
                Resume Digest Delivery
              </Button>
            ) : (
              <Button 
                data-testid="button-pause-digest"
                variant="outline" 
                size="sm" 
                className="w-full text-[10px] font-bold text-amber-400 border-amber-500/20 hover:bg-amber-500/10"
                onClick={() => pauseMutation.mutate(true)}
              >
                Pause Digest for 7 Days
              </Button>
            )}
          </div>
        </div>

        {/* SMS Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-black uppercase tracking-widest flex items-center gap-2">
              <Phone size={12} className="text-emerald-400" />
              Urgent SMS Alerts
            </h4>
            <Switch 
              data-testid="toggle-sms"
              checked={localSettings.smsEnabled} 
              onCheckedChange={v => updateField('smsEnabled', v)}
              className="scale-90"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Phone Number (Write-only)</Label>
            <Input 
              data-testid="input-sms-phone"
              type="tel"
              value={localSettings.smsPhone || ""}
              onChange={e => updateField('smsPhone', e.target.value)}
              className="h-9 font-mono text-xs bg-muted/20"
              placeholder="+1 555-000-0000"
            />
            <p className="text-[9px] text-muted-foreground/40 italic">Numbers are masked after saving for security.</p>
          </div>
        </div>

        {/* Thresholds */}
        <div className="space-y-6 pt-2">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60">Urgent Threshold</Label>
              <span className="text-xs font-black text-blue-400" data-testid="slider-urgent-threshold-value">{localSettings.urgentScoreThreshold}%</span>
            </div>
            <Slider 
              data-testid="slider-urgent-threshold"
              min={80} 
              max={100} 
              step={1} 
              value={[localSettings.urgentScoreThreshold || 90]} 
              onValueChange={([v]) => updateField('urgentScoreThreshold', v)}
              className="py-4"
            />
            <p className="text-[9px] text-muted-foreground/40 leading-relaxed">Harry will only send instant alerts for findings with a relevance score equal to or greater than this threshold.</p>
          </div>

          <div className="space-y-3">
            <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60">Urgency Categories</Label>
            <div className="grid grid-cols-2 gap-3">
              {CATEGORIES.map(cat => (
                <div key={cat.id} className="flex items-center gap-2 p-2 rounded-xl bg-muted/20 border border-border/20">
                  <Checkbox 
                    data-testid={`checkbox-category-${cat.id}`}
                    id={`cat-${cat.id}`}
                    checked={localSettings.urgentCategories?.includes(cat.id)}
                    onCheckedChange={checked => {
                      const current = localSettings.urgentCategories || [];
                      const next = checked ? [...current, cat.id] : current.filter(id => id !== cat.id);
                      updateField('urgentCategories', next);
                    }}
                  />
                  <Label htmlFor={`cat-${cat.id}`} className="text-[11px] font-bold cursor-pointer flex-1">{cat.label}</Label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Button 
          data-testid="button-save-harry-settings"
          className="w-full h-12 font-black uppercase tracking-[0.2em] bg-blue-600 hover:bg-blue-500 text-white shadow-xl shadow-blue-500/20 mt-4"
          onClick={() => saveMutation.mutate(localSettings)}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : "Save Harry Configuration"}
        </Button>
      </div>
    </div>
  );
}

// Helper to add days
function addDays(date: Date, days: number) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

import { Download } from "lucide-react";
